import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"
import { styledConfirm } from "../lib/turbo_confirm"

// Company Settings → Timesheets (Phelipe 2026-10-09): every time clock
// company setting the phone's Time Tracker Settings has. Each box, choice,
// list or typed value saves on its own through PATCH /timesheets/settings
// (the same Setting the phone changes); the answer is the whole setting, so
// every box is redrawn from it. A row with data-needs="key" (or
// "key=value", or "key!=value") only counts while that setting is on, and is
// greyed out otherwise. "Different for some people?" saves person by person.
// "Keep stops for" (2026-11-02): a shorter time asks first
// (data-confirm-shorter), and its History pop-up is redrawn after a change.
const FT_PER_M = 3.28084

export default class extends Controller {
  static targets = ["input", "historyModal", "historyList", "historyLast"]
  static values = { url: String, personUrl: String, saved: String, failed: String, settings: Object, peopleCount: Object, editable: Boolean }

  connect() {
    this.current = { ...this.settingsValue }
    this._show()
    this._localTimes(this.element)
    this._onKey = (e) => { if (e.key === "Escape" && this._historyOpen()) this.closeHistory() }
    document.addEventListener("keydown", this._onKey)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey)
  }

  async save(e) {
    const input = e.currentTarget
    if (input.type === "radio" && !input.checked) return
    const key = input.dataset.key
    const value = this._valueOf(input, key)
    if (value === undefined) return this._show()
    if (input.dataset.confirmShorter && this._shorter(value, this.current[key])) {
      const period = input.selectedOptions?.[0]?.textContent.trim() || ""
      if (!(await styledConfirm(input.dataset.confirmShorter.replaceAll("%{period}", period), input))) return this._show()
    }
    const data = await this._patch(this.urlValue, { settings: { [key]: value }, source: "company_settings" })
    if (data?.settings) {
      this.current = data.settings
      if (data.keep_history) this._redrawHistory(data.keep_history)
      showToast(this.savedValue)
    }
    this._show()
  }

  openHistory(e) {
    if (!this.hasHistoryModalTarget) return
    this._historyOpener = e?.currentTarget
    this.historyModalTarget.classList.remove("hidden")
    this.historyModalTarget.querySelector("[data-history-close]")?.focus()
  }

  closeHistory() {
    if (!this.hasHistoryModalTarget) return
    this.historyModalTarget.classList.add("hidden")
    this._historyOpener?.focus()
  }

  closeHistoryOutside(e) {
    if (e.target === this.historyModalTarget) this.closeHistory()
  }

  async savePerson(e) {
    const select = e.currentTarget
    const before = select.dataset.saved ?? select.querySelector("option[selected]")?.value ?? ""
    const data = await this._patch(this.personUrlValue, { user_id: Number(select.dataset.userId), clock_in_requires: select.value })
    if (data) {
      select.dataset.saved = select.value
      showToast(this.savedValue)
    } else {
      select.value = before
    }
  }

  _valueOf(input, key) {
    if (input.dataset.person !== undefined) {
      return Array.from(this.element.querySelectorAll(`input[data-key="${key}"][data-person]`))
        .filter((box) => box.checked).map((box) => Number(box.dataset.person))
    }
    if (input.type === "checkbox") return input.checked
    if (input.dataset.type === "number") {
      if (input.value === "") return "" // "Off" (overtime)
      const n = Number(input.value)
      if (!Number.isFinite(n)) return undefined
      // Job area size is typed in the company's unit and kept in meters.
      if (key === "job_radius_m") return Math.round((input.dataset.unit === "ft" ? n / FT_PER_M : n) * 10) / 10
      return n
    }
    return input.value
  }

  // A shorter time deletes stops, so it asks first. 0 is Forever, the longest.
  _shorter(next, now) {
    const n = Number(next)
    const c = Number(now) || 0
    return n !== 0 && (c === 0 || n < c)
  }

  _historyOpen() {
    return this.hasHistoryModalTarget && !this.historyModalTarget.classList.contains("hidden")
  }

  _redrawHistory({ list, last }) {
    if (this.hasHistoryListTarget && list != null) this.historyListTarget.innerHTML = list
    if (this.hasHistoryLastTarget && last != null) this.historyLastTarget.innerHTML = last
    this._localTimes(this.element)
  }

  // History times in the viewer's own clock and language.
  _localTimes(root) {
    root.querySelectorAll("time[data-local-time]").forEach((el) => {
      const d = new Date(el.getAttribute("datetime"))
      if (Number.isNaN(d.getTime())) return
      const opts = { year: "numeric", month: "short", day: "numeric" }
      if (el.dataset.localTime !== "date") Object.assign(opts, { hour: "numeric", minute: "2-digit" })
      el.textContent = d.toLocaleString(undefined, opts)
    })
  }

  async _patch(url, body) {
    try {
      const res = await fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || this.failedValue)
      return data
    } catch (err) {
      showToast(err.message || this.failedValue, "alert")
      return null
    }
  }

  // Every box back the way the server has it, and the rows that wait on
  // another setting greyed out while it's off.
  _show() {
    const s = this.current
    this.inputTargets.forEach((input) => {
      const key = input.dataset.key
      if (document.activeElement === input && input.type === "text") return
      if (input.dataset.person !== undefined) input.checked = (s[key] || []).map(Number).includes(Number(input.dataset.person))
      else if (input.type === "checkbox") input.checked = s[key] === true
      else if (input.type === "radio") input.checked = String(s[key]) === input.value
      else if (key === "job_radius_m") {
        input.dataset.unit = s.distance_unit
        const m = Number(s.job_radius_m) || 0
        input.value = String(Math.round(s.distance_unit === "ft" ? m * FT_PER_M : m))
      } else if (input.tagName === "SELECT") {
        const want = s[key] == null ? "" : String(s[key])
        const match = Array.from(input.options).find((o) => o.value === want || (o.value !== "" && Number(o.value) === Number(want)))
        if (match) input.value = match.value
      } else input.value = s[key] ?? ""
    })
    this.element.querySelectorAll("[data-needs]").forEach((row) => {
      const on = row.dataset.needs.split(" ").every((need) => {
        const not = need.includes("!=")
        const [key, wanted] = need.split(not ? "!=" : "=")
        if (not) return String(s[key]) !== wanted
        return wanted === undefined ? s[key] === true : String(s[key]) === wanted
      })
      row.classList.toggle("opacity-50", !on)
      row.querySelectorAll("input, select, textarea").forEach((el) => { el.disabled = !this.editableValue || !on })
    })
    this.element.querySelectorAll("[data-count-for]").forEach((el) => {
      const n = (s[el.dataset.countFor] || []).length
      const form = n === 0 ? "zero" : n === 1 ? "one" : "other"
      el.textContent = (this.peopleCountValue[form] || this.peopleCountValue.other || "%{count}").replace("%{count}", n)
    })
    const range = this.element.querySelector("[data-radius-range]")
    if (range) range.textContent = s.distance_unit === "ft" ? range.dataset.rangeFt : range.dataset.rangeM
  }
}
